import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from "react";
import { tg } from "@/lib/telegram";

interface State {
  error: Error | null;
}

/**
 * Глобальный React ErrorBoundary. Ловит любую ошибку рендера ниже по дереву
 * и показывает осмысленный экран вместо белого. Кнопка «Перезавантажити»
 * закрывает Mini App (Telegram при следующем открытии загрузит свежий бандл).
 *
 * Без этого rendering-исключения в Mini App видны юзеру как пустой экран
 * с крутящимся ботом, что мы уже наблюдали.
 */
export class ErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // В prod сюда подключим Sentry/etc. В dev — просто console.error, чтобы
    // Web Inspector в Telegram-клиенте всё показал.
    console.error("[Inflow] Render error caught by boundary:", error, info);
    tg()?.HapticFeedback.notificationOccurred("error");
  }

  private reset = () => {
    this.setState({ error: null });
  };

  private closeApp = () => {
    tg()?.close();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 py-12 text-center">
          <div className="text-5xl">😬</div>
          <h1 className="text-xl font-semibold">Щось пішло не так</h1>
          <p className="text-sm text-hint">
            Сталася помилка в інтерфейсі. Спробуй перезавантажити Mini App.
          </p>
          <details className="w-full rounded-ios bg-surface-secondary p-3 text-left text-xs">
            <summary className="cursor-pointer text-hint">Технічні деталі</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-[0.6875rem]">
              {this.state.error.message}
            </pre>
          </details>
          <div className="mt-2 flex w-full gap-3">
            <button
              type="button"
              onClick={this.reset}
              className="flex-1 rounded-ios bg-surface-secondary px-4 py-3 font-semibold"
            >
              Спробувати знову
            </button>
            <button
              type="button"
              onClick={this.closeApp}
              className="flex-1 rounded-ios bg-accent px-4 py-3 font-semibold text-accent-text"
            >
              Закрити
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
